/**
 * Determines environment and returns related configuration
 * @returns Config related to current environment
 */
export function getEnvironment(): EnvironmentConfig {
    const { host } = window.location;

    // Topicus Education AI sandbox (Kosmos). One image serves every business line: the
    // tenant is derived from the hostname rather than baked in at build time, so adding a
    // business line is a Helm value, not a rebuild.
    //   inversable-chat.<businessline>.sandbox.ai.topicus.education
    //     -> Keycloak realm `inversable-<businessline>` on the shared Inversable Keycloak
    const sandbox = /^inversable-chat\.([a-z0-9-]+)\.sandbox\.ai\.topicus\.education$/.exec(host);
    if (sandbox) {
        const businessLine = sandbox[1];
        return {
            name: `Sandbox (${businessLine})`,
            customLoginPage: false,
            keycloak: {
                url: 'https://inversable-auth.sandbox.ai.topicus.education',
                realm: `inversable-${businessLine}`,
                clientId: 'front-end',
            },
            graphqlws: `wss://${location.hostname}/graphqlws/v1/graphql`,
            apiRootURL: `https://${location.hostname}/graphql_backend`,
        };
    }

    if (host === 'onderwijs-ai.inversable.dev') {
        // Development
        return {
            name: 'Testomgeving',
            customLoginPage: false,
            keycloak: {
                url: 'https://keycloak.inversable.cloud',
                realm: 'onderwijs-ai',
                clientId: 'chatbot-dev',
            },
            graphqlws: `wss://${location.hostname}/graphqlws/v1/graphql`,
            apiRootURL: `https://${location.hostname}/graphql_backend`,
        };
    }
    return {
        // Local development
        name: 'Lokaal',
        customLoginPage: false,
        keycloak: {
            url: 'http://localhost:8080',
            realm: 'rag-chat',
            clientId: 'front-end',
        },
        graphqlws: 'ws://localhost:8085/v1/graphql',
        apiRootURL: 'http://localhost:3200',
    };
}

/**
 * Environment-specific variables
 */
interface EnvironmentConfig {
    /**
     * Name of environment to show under user name in top bar
     */
    name: string;
    /**
     * True if this environment has a custom login page
     * This page should be present in ./public/customers/<>/login.html
     */
    customLoginPage?: boolean;
    /**
     * Keycloak configuration
     */
    keycloak: {
        url: string;
        realm: string;
        clientId: string;
    };
    /**
     * Endpoint hasura accepts connection on
     */
    graphqlws: string;
    /**
     * Root URL of backend api
     */
    apiRootURL: string;
    /**
     * Optionally provide a sentry DSN and code version. If this variable is unset for an environment nog logs will be sent.
     */
    sentry?: {
        dsn: string;
        // This hardcoded string is replaced by the CI_COMMIT_SHORT_SHA variable during the build_docker_images step
        release: 'VERSION';
        tunnel: string;
    };
}
